import {runInAction} from 'mobx';
import Websocket from 'modules/Websocket';

import LineInterface from 'app/interfaces/LineInterface';
import {
    configStore,
    giveawayStore,
    promoActionStore,
    storiesStore,
    streamStore,
    userStore,
    casinoGamesStore,
    articlesStore,
    userBonusesStore,
    cashoutsStore,
    steamTradeStore,
    linesService,
    quickBetStore
} from 'app/store/global';
import UserInterface from 'app/interfaces/UserInterface';
import {isGuest} from 'app/utils';
import NotificationStore from 'app/store/notifications';
import {LinePayloadInterface} from 'app/interfaces/LinePayloadInterface';
import {DelayedBetCancelledInterface} from 'app/interfaces/DelayedBetCancelledInterface';
import {ChatMessagePayLoadInterface} from 'app/interfaces/stores/subcribe/ChatMessagePayLoadInterface';

export interface DataEventInterface {
    data: {
        event: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload: any
        type: string
        group: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any
    }
}

const pendingLinesCoefByLineId = new Map<number, LineInterface>();

let linesCoefFlushScheduled = false;

const scheduleFlushLinesCoef = (): void => {
    if (linesCoefFlushScheduled) {
        return;
    }
    linesCoefFlushScheduled = true;
    requestAnimationFrame(() => {
        linesCoefFlushScheduled = false;
        const payloads = Array.from(pendingLinesCoefByLineId.values());
        pendingLinesCoefByLineId.clear();
        if (payloads.length === 0) {
            return;
        }
        runInAction(() => {
            payloads.forEach((linePayload: LineInterface) => {
                linesService.listen(linePayload);
                quickBetStore.handleWsLineUpdate(linePayload);
            });
        });
    });
};

const queueLinesCoefUpdate = (payload: LineInterface): void => {
    pendingLinesCoefByLineId.set(payload.id, payload);
    scheduleFlushLinesCoef();
};

export const unsubscribe = (subscription: string): null => {
    if (subscription) {
        Websocket.unsubscribe(subscription);
    }

    return null;
};

export const listenGeneral = (): void => {
    subscribeGeneral(({data: {event, payload}}) => {
        switch (event) {
        case 'lines_update_coef':
            queueLinesCoefUpdate(payload);
            break;
        case 'giveaways_update':
            giveawayStore.updateGiveaway(payload);
            break;
        case 'main_articles_update':
            articlesStore.listenMain(payload);
            break;
        case 'promo_new_articles_update':
            articlesStore.debouncedSetPromoNewData(payload);
            break;
        case 'promo_actions_update':
            promoActionStore.update(payload);
            break;
        case 'maintenance_update':
            configStore.setMaintenance(Boolean(payload.maintenance));
            break;
        case 'stories_update':
            storiesStore.listenUpdate(payload);
            break;
        case 'stories_delete':
            storiesStore.deleteStory(payload.id);
            break;
        case 'streams_update':
            streamStore.add(payload);
            break;
        case 'streams_delete':
            streamStore.delete(payload.id);
            break;
        default: break;
        }
    });
};

export const listenMaintenance = (): void => {
    subscribeGeneral(({data: {event, payload}}) => {
        if (event === 'maintenance_update') {
            configStore.setMaintenance(Boolean(payload.maintenance));
        }
    });
};

export const listenUser = (): void => {
    if (isGuest()) {
        return;
    }

    // eslint-disable-next-line complexity
    subscribeUser(userStore.user, ({data: {event, payload}}) => {
        switch (event) {
        case 'story_update':
            storiesStore.listenUserStory(payload);
            break;
        case 'story_image_update':
            storiesStore.updateUserStoryImage(payload);
            break;
        case 'casino_games_update':
            casinoGamesStore.listenCasinoGameUpdate(payload);
            break;
        case 'casino_session_update':
            casinoGamesStore.listenCasinoSession(payload);
            break;
        case 'giveaway_update':
            giveawayStore.listenUserGiveaway(payload);
            break;
        case 'bonus_admin_cancel':
            userBonusesStore.listen();
            break;
        case 'bonus_create':
            userBonusesStore.bonus_notice(payload);
            break;
        case 'free_spin_create':
            userBonusesStore.free_spin_notice(payload);
            break;
        case 'free_spin_cancelled':
            userBonusesStore.freeSpinCancelledNotice(payload);
            break;
        case 'free_spin_cancel_error':
            userBonusesStore.freeSpinCancelErrorNotice(payload);
            break;
        case 'promo_action_update':
            promoActionStore.update(payload);
            break;
        case 'user_update':
            userStore.listen(payload);
            break;
        case 'cashout_update':
            cashoutsStore.updateCashout(payload);
            break;
        case 'steam_trade_update':
            steamTradeStore.updateElementCollection(payload);
            break;
        case 'frozen_balance_toast':
            userStore.frozenBalanceToast(payload);
            break;
        case 'incubator_initial_presets_update':
            userStore.updateIncubatorInitialPresets(payload);
            break;
        case 'main_articles_update':
            articlesStore.listenMain(payload);
            break;
        case 'promo_new_articles_update':
            articlesStore.debouncedSetPromoNewData(payload);
            break;
        default: break;
        }
    });
};

export const listenNotifications = (): void => {
    subscribeNotifications(({data}) => {
        NotificationStore.listen(data);
    });
};

export const listenUserNotifications = (): void => {
    if (isGuest()) {
        return;
    }

    subscribeUserNotification(userStore.user, ({data}) => {
        NotificationStore.listen(data);
    });
};

export const subscribeGeneral = (onEvent: (data: DataEventInterface) => void):
    string => Websocket.subscribe(':general', onEvent);

export const subscribeUser = (user: UserInterface, onEvent: (data: DataEventInterface) => void):
    string => Websocket.subscribe(`$user-${user.id}`, onEvent);

export const subscribeNotifications = (onEvent: (data: DataEventInterface) => void):
    string => Websocket.subscribe(':notifications', onEvent);

export const subscribeUserNotification = (user: UserInterface, onEvent: (data: DataEventInterface) => void):
    string => Websocket.subscribe(`$notification-${user.id}`, onEvent);

export const subscribeCryptoAddress = (user: UserInterface, onEvent: (data: DataEventInterface) => void):
    string => Websocket.subscribe(`$crypto-address-${user.id}`, onEvent);

export const subscribeLineUpdateCoef = (hash_id: string, onEvent: (data: LinePayloadInterface) => void):
    string => Websocket.subscribe(`:line-${hash_id}`, onEvent);

export const subscribeDelayedBet = (userId: number, onEvent: (data: DelayedBetCancelledInterface) => void):
    string => Websocket.subscribe(`$bet-of-${userId}`, onEvent);

export const subscribeDelayedExpressBet = (userId: number, onEvent: (data: DelayedBetCancelledInterface) => void):
    string => Websocket.subscribe(`$express-bet-of-${userId}`, onEvent);

export const subcribeChat = (onEvent: (data: ChatMessagePayLoadInterface) => void):
    string => Websocket.subscribe(':chat', onEvent);
